import { useEffect, useState } from 'react';
import { Fingerprint, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { CompleteTrainingInput } from '../../types/training.types';

interface TrainingSignatureDialogProps {
  open: boolean;
  documentName: string;
  defaultUsername: string;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: CompleteTrainingInput) => Promise<void>;
}

export function TrainingSignatureDialog({
  open,
  documentName,
  defaultUsername,
  isPending,
  onOpenChange,
  onConfirm,
}: TrainingSignatureDialogProps) {
  const [reason, setReason] = useState('');
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!open) return;
    setReason('');
    setUsername(defaultUsername);
    setPassword('');
  }, [defaultUsername, open]);

  const canSubmit = reason.trim().length >= 3
    && username.trim().length > 0
    && password.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <Fingerprint className="h-5 w-5" />
          </div>
          <DialogTitle>Sign training qualification</DialogTitle>
          <DialogDescription>
            Your credentials bind the qualification decision to{' '}
            <span className="font-medium text-slate-800">{documentName}</span>. The UTC
            manifestation and reason become immutable evidence.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-teal-100 bg-teal-50/70 px-3 py-2 text-sm text-teal-900">
            <div className="flex items-center gap-2 font-medium">
              <ShieldCheck className="h-4 w-4" />
              Signature meaning: qualification
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="training-signature-reason">Reason</Label>
            <Textarea
              id="training-signature-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="I completed the assigned reading and assessment…"
              rows={3}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="training-signature-username">Username</Label>
              <Input
                id="training-signature-username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training-signature-password">Password</Label>
              <Input
                id="training-signature-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="bg-teal-700 text-white hover:bg-teal-800"
            disabled={!canSubmit || isPending}
            onClick={() => onConfirm({
              meaning: 'qualification',
              reason: reason.trim(),
              username: username.trim(),
              password,
            })}
          >
            {isPending ? 'Signing…' : 'Sign and complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
